import { CheckCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Flex, Form, Input, Row, Steps, Tag, Typography } from "antd";
import { useState } from "react";
import { Navigate } from "react-router";
import { api, unwrap, type ChannelPlatformName } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { Wordmark } from "@/components/Logo";
import { useI18n } from "@/i18n";
import { useConnectResult } from "@/pages/channels/Channels";
import { PLATFORMS, useConnect, useConnections, usePlatforms } from "@/pages/channels/hooks";
import { KeyConnectModal } from "@/pages/channels/KeyConnectModal";
import { PlatformLogo } from "@/pages/channels/PlatformBadge";
import { WarehouseFields, type WarehouseValues } from "@/pages/settings/WarehouseFields";

/**
 * Daftar → Verifikasi → Buat perusahaan → Hubungkan marketplace → Tambah gudang →
 * Beranda (App Flow §4). The server keeps the current step, so a reload or a
 * second device resumes in the right place.
 */
export function Onboarding() {
  const { t } = useI18n();
  const { me } = useAuth();
  if (!me) return null;
  const step = !me.workspace ? 0 : me.workspace.onboardingStep === "marketplace" ? 1 : me.workspace.onboardingStep === "warehouse" ? 2 : 3;
  if (step === 3) return <Navigate to="/app" replace />;

  return (
    <main style={{ minHeight: "100vh" }}>
      <Flex justify="space-between" align="center" style={{ padding: "20px 32px" }}>
        <Wordmark size={30} />
        <LanguageSwitch />
      </Flex>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "16px 16px 64px" }}>
        <Typography.Title level={2} style={{ fontSize: 28 }}>
          {t("onboarding.title")}
        </Typography.Title>
        <Steps
          current={step}
          style={{ margin: "24px 0 32px" }}
          items={[
            { title: t("onboarding.step.company") },
            { title: t("onboarding.step.marketplace") },
            { title: t("onboarding.step.warehouse") },
          ]}
        />
        {step === 0 && <CompanyStep />}
        {step === 1 && <MarketplaceStep />}
        {step === 2 && <WarehouseStep />}
      </div>
    </main>
  );
}

function CompanyStep() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<{ name: string }>();
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async ({ name }: { name: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      await signIn(await unwrap(api.POST("/v1/workspaces", { body: { name } })));
    } catch (err) {
      if (!applyFieldErrors(form, err)) setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title={t("onboarding.company.title")}>
      <Typography.Paragraph type="secondary">{t("onboarding.company.body")}</Typography.Paragraph>
      {error !== null && <Alert type="error" showIcon title={errorText(error)} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false} disabled={submitting}>
        <Form.Item
          name="name"
          label={t("onboarding.company.label")}
          rules={[{ required: true, whitespace: true, min: 2, message: t("field.too_short") }, { max: 120, message: t("field.too_long") }]}
        >
          <Input autoFocus />
        </Form.Item>
        <Flex justify="end">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {t("common.continue")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
}

function useAdvance() {
  const { refetchMe } = useAuth();
  const errorText = useErrorText();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const advance = async (step: "marketplace" | "warehouse", action: "complete" | "skip") => {
    setBusy(true);
    setError(null);
    try {
      await unwrap(api.POST("/v1/workspace/onboarding", { body: { step, action } }));
      await refetchMe();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return { advance, error, busy };
}

function MarketplaceStep() {
  const { t } = useI18n();
  const { advance, error, busy } = useAdvance();
  const platforms = usePlatforms();
  const connections = useConnections();
  const connect = useConnect();
  useConnectResult();
  const available = new Map(platforms.data?.items.map((p) => [p.platform, p.available]));
  const connectedCount = (p: ChannelPlatformName) => connections.data?.items.filter((c) => c.platform === p).length ?? 0;
  const anyAvailable = platforms.data?.items.some((p) => p.available) ?? true;
  const anyConnected = (connections.data?.items.length ?? 0) > 0;

  return (
    <Card title={t("onboarding.marketplace.title")}>
      <Typography.Paragraph type="secondary">{t("onboarding.marketplace.body")}</Typography.Paragraph>
      {!anyAvailable && <Alert type="info" showIcon title={t("onboarding.marketplace.notYet")} style={{ marginBottom: 24 }} />}
      <Row gutter={[16, 16]}>
        {PLATFORMS.map((p) => {
          const n = connectedCount(p);
          return (
            <Col xs={12} md={6} key={p}>
              <Card size="small" styles={{ body: { display: "grid", justifyItems: "center", gap: 12, padding: 20 } }}>
                <PlatformLogo platform={p} size={32} />
                <Typography.Text strong>{t(`platform.${p}`)}</Typography.Text>
                {n > 0 ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    {t("onboarding.marketplace.connected")}
                    {n > 1 ? ` (${n})` : ""}
                  </Tag>
                ) : available.get(p) ? (
                  <Button size="small" loading={connect.isPending && connect.variables === p} onClick={() => connect.mutate(p)}>
                    {t("onboarding.marketplace.connect")}
                  </Button>
                ) : (
                  <Tag>{t("channels.unavailable")}</Tag>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
      {error && <Alert type="error" showIcon title={error} style={{ marginTop: 16 }} />}
      <Flex justify="end" style={{ marginTop: 24 }}>
        <Button type="primary" loading={busy} onClick={() => void advance("marketplace", anyConnected ? "complete" : "skip")}>
          {anyConnected ? t("common.continue") : t("common.skip")}
        </Button>
      </Flex>
      {/* Key-auth marketplaces (Blibli): useConnect's /app/channels?connect= lands back here during onboarding. */}
      <KeyConnectModal />
    </Card>
  );
}

function WarehouseStep() {
  const { t } = useI18n();
  const { advance, error, busy } = useAdvance();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<WarehouseValues>();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (values: WarehouseValues) => {
    setSaving(true);
    setSaveError(null);
    try {
      await unwrap(api.POST("/v1/warehouses", { body: values }));
      // Once onboarding reads "done", the page redirects itself to the app.
      await advance("warehouse", "complete");
    } catch (err) {
      if (!applyFieldErrors(form, err)) setSaveError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={t("onboarding.warehouse.title")}>
      <Typography.Paragraph type="secondary">{t("onboarding.warehouse.body")}</Typography.Paragraph>
      {(saveError ?? error) && <Alert type="error" showIcon title={saveError ?? error} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={save} requiredMark={false} disabled={saving || busy}>
        <WarehouseFields creating />
        <Flex justify="end" gap={12}>
          <Button onClick={() => void advance("warehouse", "skip")} loading={busy && !saving}>
            {t("common.skip")}
          </Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t("common.save")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
}
